import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { Truck, Key, CheckCircle, XCircle, Eye, EyeOff, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import woltLogo from '../assets/logos/logo_wolt.png';

interface WoltDriveConfigData {
  wolt_drive_enabled: boolean;
  wolt_drive_venue_id: string | null;
  has_api_key: boolean;
}

const WoltDriveConfig: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<WoltDriveConfigData>({
    wolt_drive_enabled: false,
    wolt_drive_venue_id: '',
    has_api_key: false
  });
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (store?.id) {
      fetchConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchConfig = async () => {
    if (!store?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/wolt-drive/${store.id}/config`);
      setConfig({
        wolt_drive_enabled: response.data.wolt_drive_enabled || false,
        wolt_drive_venue_id: response.data.wolt_drive_venue_id || '',
        has_api_key: response.data.has_api_key || false
      });
    } catch (error) {
      console.error('Error fetching Wolt Drive config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!store?.id) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const updateData: any = {
        wolt_drive_enabled: config.wolt_drive_enabled,
        wolt_drive_venue_id: config.wolt_drive_venue_id
      };
      
      // Only include API key if it was changed
      if (apiKey) {
        updateData.wolt_drive_api_key = apiKey;
      }
      
      await api.put(`/wolt-drive/${store.id}/config`, updateData);
      
      setMessage({ type: 'success', text: t('settings:woltDrive.settingsSavedSuccess') });
      setApiKey(''); // Clear the API key field
      fetchConfig(); // Refresh to get updated has_api_key
    } catch (error: any) {
      console.error('Error saving Wolt Drive config:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || t('settings:woltDrive.settingsSaveError') });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!store?.id) return;
    
    // Check if credentials are configured before enabling
    if (!config.wolt_drive_enabled && (!config.wolt_drive_venue_id || !config.has_api_key)) {
      setMessage({ type: 'error', text: t('settings:woltDrive.credentialsRequired') });
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/wolt-drive/${store.id}/config`, {
        wolt_drive_enabled: !config.wolt_drive_enabled
      });
      setConfig(prev => ({ ...prev, wolt_drive_enabled: !prev.wolt_drive_enabled }));
      setMessage({ type: 'success', text: config.wolt_drive_enabled ? t('settings:woltDrive.deactivated') : t('settings:woltDrive.activated') });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || t('settings:woltDrive.toggleError') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-1 rounded-xl p-6 border border-line">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-t3 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 dark:bg-surface-1 rounded-xl border border-line overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-surface-3 dark:hover:bg-surface-2/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <img src={woltLogo} alt="Wolt" className="w-10 h-10 object-contain" />
          <div>
            <h3 className="font-semibold text-t1">Wolt Drive</h3>
            <p className="text-sm text-t3">{t('settings:woltDrive.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {config.wolt_drive_enabled ? (
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              {t('common:active')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-t4 text-sm">
              <XCircle className="w-4 h-4" />
              {t('common:inactive')}
            </span>
          )}
          <svg 
            className={`w-5 h-5 text-t3 transition-transform ${expanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-line p-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-2 dark:bg-surface-2/50 rounded-lg">
            <div>
              <p className="font-medium text-t1">{t('settings:woltDrive.enableTitle')}</p>
              <p className="text-sm text-t3">{t('settings:woltDrive.enableDesc')}</p>
            </div>
            <button
              onClick={handleToggleEnabled}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.wolt_drive_enabled ? 'bg-blue-500' : 'bg-surface-3'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.wolt_drive_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">{t('settings:woltDrive.howToGetCredentials')}</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>{t('settings:woltDrive.step1')}</li>
                  <li>{t('settings:woltDrive.step2')}</li>
                  <li>{t('settings:woltDrive.step3')}</li>
                </ol>
                <a 
                  href="https://developer.wolt.com/docs/api/wolt-drive" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  Wolt Drive Documentation
                </a>
              </div>
            </div>
          </div>

          {/* Credentials Form */}
          <div className="space-y-4">
            <h4 className="font-medium text-t1 flex items-center gap-2">
              <Key className="w-4 h-4" />
              API Credentials
            </h4>

            {/* Venue ID */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Venue ID
              </label>
              <input
                type="text"
                value={config.wolt_drive_venue_id || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, wolt_drive_venue_id: e.target.value }))}
                placeholder={t('settings:woltDrive.venueIdPlaceholder')}
                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-t4 mt-1">
                {t('settings:woltDrive.venueIdHint')}
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                API Key
                {config.has_api_key && (
                  <span className="ml-2 text-green-600 text-xs font-normal">✓ {t('settings:woltDrive.apiKeySaved')}</span>
                )}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={config.has_api_key
                    ? t('settings:woltDrive.apiKeyKeepPlaceholder')
                    : t('settings:woltDrive.apiKeyBearerPlaceholder')
                  }
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-t3 hover:text-t2"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-t4 mt-1">
                {t('settings:woltDrive.apiKeyHint')}
              </p>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              {message.text}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-line">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common:saving')}
                </>
              ) : (
                <>
                  <Truck className="w-4 h-4" />
                  {t('settings:woltDrive.saveCredentials')}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WoltDriveConfig;
